import { VariantIcon } from "@xola/icons";
import { Alert, flash, Modal, SubmitButton, Table, Tooltip } from "@xola/ui-kit";
import { useLockFn, useMemoizedFn } from "ahooks";
import { uniqueId } from "lodash";
import pluralize from "pluralize";
import React, { useMemo, useState } from "react";
import { InventoryRow, ProductObject } from "types";
import { AdjustAction, AdjustmentRow, getMaxQty, InventoryStats, REASONS, RowStatus } from "./adjustInventoryTypes";
import { AdjustmentTableRow } from "./AdjustmentTableRow";
import { InventoryStatsGrid } from "./InventoryStatsGrid";
import { xolaUri } from "@/api/xolaUri";
import { ProductImage } from "@/components/Common/ProductImage";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { ManageActionButton } from "@/components/Dashboard/Roster/ManageActionButton";
import { LotOperation, useAdjustInventoryLot } from "@/hooks/resources/useAdjustInventoryLot";

export const computeRunningStates = (initial: InventoryStats, rows: AdjustmentRow[]): InventoryStats[] => {
    let state = { ...initial };
    return rows.map((row) => {
        switch (row.action) {
            case AdjustAction.MOVE_TO_UNAVAILABLE:
                state = {
                    ...state,
                    available: state.available - row.qty,
                    unavailable: state.unavailable + row.qty,
                    onHand: Math.max(0, state.onHand - row.qty),
                };
                break;
            case AdjustAction.MOVE_TO_AVAILABLE:
                state = {
                    ...state,
                    available: state.available + row.qty,
                    unavailable: state.unavailable - row.qty,
                    onHand: state.onHand + row.qty,
                };
                break;
            case AdjustAction.ADD_INVENTORY:
                state = { ...state, available: state.available + row.qty, onHand: state.onHand + row.qty };
                break;
            case AdjustAction.REMOVE_FROM_AVAILABLE:
                state = { ...state, available: state.available - row.qty, onHand: state.onHand - row.qty };
                break;
            case AdjustAction.REMOVE_FROM_UNAVAILABLE:
                state = { ...state, unavailable: state.unavailable - row.qty };
                break;
        }
        return { ...state };
    });
};

export const buildOperations = (rows: AdjustmentRow[], lotId: string): LotOperation[] =>
    rows.map((row) => ({
        id: row.id,
        op:
            row.action === AdjustAction.REMOVE_FROM_UNAVAILABLE || row.action === AdjustAction.REMOVE_FROM_AVAILABLE
                ? "permanent_removal"
                : "increment",
        data: {
            id: lotId,
            field: getField(row.action),
            value: getValue(row.action, row.qty),
            reason: row.reason,
        },
    }));

const getField = (action: AdjustAction): string => {
    switch (action) {
        case AdjustAction.MOVE_TO_UNAVAILABLE:
        case AdjustAction.MOVE_TO_AVAILABLE:
            return "unavailable";
        case AdjustAction.ADD_INVENTORY:
        case AdjustAction.REMOVE_FROM_AVAILABLE:
            return "count";
        case AdjustAction.REMOVE_FROM_UNAVAILABLE:
            return "unavailable";
    }
};

const getValue = (action: AdjustAction, qty: number): number => {
    switch (action) {
        case AdjustAction.MOVE_TO_UNAVAILABLE:
        case AdjustAction.ADD_INVENTORY:
            return qty;
        case AdjustAction.MOVE_TO_AVAILABLE:
            return -qty;
        case AdjustAction.REMOVE_FROM_AVAILABLE:
        case AdjustAction.REMOVE_FROM_UNAVAILABLE:
            return qty;
    }
};

const isRemovalAction = (row: AdjustmentRow) =>
    row.action === AdjustAction.REMOVE_FROM_UNAVAILABLE || row.action === AdjustAction.REMOVE_FROM_AVAILABLE;

const makeRow = (action: AdjustAction): AdjustmentRow => ({
    id: uniqueId("row"),
    action,
    reason: REASONS[action][0],
    qty: 1,
});

const TruncatedHeader = ({ label, tooltip }: { label: string; tooltip: string }) => (
    <Tooltip content={tooltip}>
        <span className="block truncate">{label}</span>
    </Tooltip>
);

const ProductHeader = ({ lot }: { lot: InventoryRow }) => (
    <div className="mb-6 flex items-center gap-3 rounded bg-gray-light p-4">
        <ProductImage
            product={{
                id: lot.productId,
                object: ProductObject.WARE,
                ...(lot.productPhotoId && { photo: { id: lot.productPhotoId } }),
            }}
            src={lot.thumbnailUrl ? `${xolaUri}${lot.thumbnailUrl}` : undefined}
            size="large"
            className="!mr-0 flex-shrink-0"
        />
        <div>
            <p className="h-8 font-bold">{lot.productName}</p>
            <p className="flex h-8 items-center gap-1 text-sm text-gray-dark">
                <VariantIcon className="h-3 w-3 text-gray-dark" />
                {lot.variantLabel}
            </p>
        </div>
    </div>
);

interface AdjustmentsSectionProps {
    rows: AdjustmentRow[];
    runningStates: InventoryStats[];
    initial: InventoryStats;
    rowValidities: boolean[];
    rowStatuses: Record<string, RowStatus>;
    rowErrors: Record<string, string>;
    hasInvalidRows: boolean;
    onUpdateRow: (id: string, patch: Partial<Omit<AdjustmentRow, "id">>) => void;
    onDeleteRow: (id: string) => void;
    onAddRow: () => void;
}

const AdjustmentsSection = ({
    rows,
    runningStates,
    initial,
    rowValidities,
    rowStatuses,
    rowErrors,
    hasInvalidRows,
    onUpdateRow,
    onDeleteRow,
    onAddRow,
}: AdjustmentsSectionProps) => (
    <>
        <div className="mb-2 overflow-hidden rounded">
            <Table className="w-full table-fixed border-none">
                <Table.Head>
                    <Table.Row className="text-gray-dark">
                        <Table.Header className="w-36 !px-1 text-left !text-[12px]">Action</Table.Header>
                        <Table.Header className="w-24 !px-1 text-left !text-[12px]">Reason</Table.Header>
                        <Table.Header className="w-16 !px-4 text-left !text-[12px]">Quantity</Table.Header>
                        <Table.Header className="w-10 !px-1 !text-[12px]">
                            <TruncatedHeader label="Available" tooltip="Available" />
                        </Table.Header>
                        <Table.Header className="w-10 !px-1 !text-[12px]">
                            <TruncatedHeader label="Unavailable" tooltip="Unavailable" />
                        </Table.Header>
                        <Table.Header className="w-10 !px-1 !text-[12px]">
                            <TruncatedHeader label="On-Hand" tooltip="On-Hand" />
                        </Table.Header>
                        <Table.Header className="w-6 !text-[12px]" />
                        <Table.Header className="w-8 !px-1 text-center !text-[12px]">Status</Table.Header>
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {rows.map((row, index) => (
                        <AdjustmentTableRow
                            key={row.id}
                            row={row}
                            preview={runningStates[index]}
                            stateBefore={index === 0 ? initial : runningStates[index - 1]}
                            isInvalid={!rowValidities[index]}
                            status={rowStatuses[row.id] ?? "idle"}
                            error={rowErrors[row.id]}
                            onUpdate={onUpdateRow}
                            onDelete={onDeleteRow}
                        />
                    ))}
                </Table.Body>
            </Table>
        </div>
        {hasInvalidRows && (
            <Alert color="danger" className="mb-2 mt-2 text-sm">
                Some adjustments cannot be applied — there is not enough inventory for the selected action. Please
                update or remove the highlighted rows.
            </Alert>
        )}
        <ManageActionButton text="Add Adjustment" onClick={onAddRow} />
    </>
);

export interface AdjustInventoryModalProps {
    lot: InventoryRow;
    defaultAction: AdjustAction;
    isOpen: boolean;
    onClose: (didApply?: boolean) => void;
}

export const AdjustInventoryModal = ({ lot, defaultAction, isOpen, onClose }: AdjustInventoryModalProps) => {
    const [rows, setRows] = useState<AdjustmentRow[]>(() => [makeRow(defaultAction)]);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const { mutateAsync: adjustLot, isLoading, isSuccess } = useAdjustInventoryLot();
    const [rowStatuses, setRowStatuses] = useState<Record<string, RowStatus>>({});
    const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

    const initial = useMemo<InventoryStats>(
        () => ({ available: lot.available, unavailable: lot.unavailable, onHand: lot.onHand }),
        [lot.available, lot.unavailable, lot.onHand],
    );

    const runningStates = useMemo(() => computeRunningStates(initial, rows), [initial, rows]);
    const finalState = useMemo(() => runningStates.at(-1) ?? initial, [runningStates, initial]);

    const pendingRows = useMemo(() => rows.filter((row) => rowStatuses[row.id] !== "success"), [rows, rowStatuses]);
    const pendingRowCount = pendingRows.length;

    const removeQty = useMemo(
        () => pendingRows.filter((row) => isRemovalAction(row)).reduce((sum, row) => sum + row.qty, 0),
        [pendingRows],
    );

    const rowValidities = useMemo(
        () =>
            rows.map((row, index) => {
                if (rowStatuses[row.id] === "success") {
                    return true;
                }
                const stateBefore = index === 0 ? initial : runningStates[index - 1];
                const maxQty = getMaxQty(row.action, stateBefore);
                return maxQty > 0 && row.qty <= maxQty;
            }),
        [rows, rowStatuses, initial, runningStates],
    );

    const hasInvalidRows = useMemo(() => rowValidities.some((valid) => !valid), [rowValidities]);

    const addRow = useMemoizedFn(() => setRows((previous) => [...previous, makeRow(defaultAction)]));

    const updateRow = useMemoizedFn((id: string, patch: Partial<Omit<AdjustmentRow, "id">>) => {
        setRows((previous) =>
            previous.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                const updated = { ...row, ...patch };
                if (patch.action && patch.action !== row.action) {
                    updated.reason = REASONS[patch.action][0];
                }
                return updated;
            }),
        );
        if (rowStatuses[id] === "error") {
            setRowStatuses((previous) => ({ ...previous, [id]: "idle" }));
            setRowErrors((previous) => Object.fromEntries(Object.entries(previous).filter(([key]) => key !== id)));
        }
    });

    const deleteRow = useMemoizedFn((id: string) => setRows((previous) => previous.filter((row) => row.id !== id)));

    const applyAdjustments = useLockFn(async () => {
        setRowStatuses((previous) => {
            const next = { ...previous };
            for (const row of pendingRows) {
                next[row.id] = "loading";
            }
            return next;
        });

        try {
            const data = await adjustLot({
                errorStrategy: "continue_on_failure",
                ops: buildOperations(pendingRows, lot.id),
                productId: lot.productId,
            });

            const newStatuses: Record<string, RowStatus> = {};
            const newErrors: Record<string, string> = {};

            for (const result of data.results) {
                newStatuses[result.id] = result.status === "success" ? "success" : "error";
                if (result.status === "error") {
                    newErrors[result.id] = result.error?.message ?? "Operation failed";
                }
            }

            setRowStatuses((previous) => ({ ...previous, ...newStatuses }));
            setRowErrors(newErrors);

            if (data.results.every((r) => r.status === "success")) {
                onClose(true);
            }
        } catch {
            setRowStatuses((previous) => {
                const next = { ...previous };
                for (const row of pendingRows) {
                    if (next[row.id] === "loading") {
                        next[row.id] = "idle";
                    }
                }
                return next;
            });
            flash.danger("Failed to apply adjustments. Please try again.");
        }
    });

    const handleSubmitClick = () => {
        if (removeQty > 0) {
            setIsConfirmingDelete(true);
        } else {
            void applyAdjustments();
        }
    };

    return (
        <Modal size="huge" isOpen={isOpen} className="!p-4" onClose={() => onClose()}>
            <Modal.Header>Adjust Inventory</Modal.Header>
            <Modal.Body>
                <ProductHeader lot={lot} />

                <h3 className="text-gray-700 mb-3 text-sm font-semibold">Current Inventory</h3>
                <div className="mb-6">
                    <InventoryStatsGrid
                        available={lot.available}
                        unavailable={lot.unavailable}
                        committed={lot.committed}
                        onHand={lot.onHand}
                    />
                </div>

                <h3 className="text-gray-700 mb-3 text-sm font-semibold">Adjustments</h3>
                <AdjustmentsSection
                    rows={rows}
                    runningStates={runningStates}
                    initial={initial}
                    rowValidities={rowValidities}
                    rowStatuses={rowStatuses}
                    rowErrors={rowErrors}
                    hasInvalidRows={hasInvalidRows}
                    onUpdateRow={updateRow}
                    onDeleteRow={deleteRow}
                    onAddRow={addRow}
                />

                <h3 className="text-gray-700 mb-3 mt-6 font-semibold">Inventory After Adjustments</h3>
                <InventoryStatsGrid
                    available={finalState.available}
                    unavailable={finalState.unavailable}
                    committed={lot.committed}
                    baseline={initial}
                    onHand={finalState.onHand}
                />
            </Modal.Body>
            <Modal.Footer>
                <SecondaryButton onClick={() => onClose()}>Cancel</SecondaryButton>
                <SubmitButton
                    data-testid="submit-adjustments-button"
                    disabled={pendingRowCount === 0 || hasInvalidRows}
                    isLoading={isLoading}
                    isSuccess={isSuccess}
                    onClick={handleSubmitClick}
                >
                    Apply {pluralize("Adjustment", pendingRowCount, true)}
                </SubmitButton>
            </Modal.Footer>
            <DeleteInventoryConfirmModal
                isOpen={isConfirmingDelete}
                qty={removeQty}
                isLoading={isLoading}
                onClose={() => setIsConfirmingDelete(false)}
                onConfirm={() => {
                    setIsConfirmingDelete(false);
                    void applyAdjustments();
                }}
            />
        </Modal>
    );
};

interface DeleteInventoryConfirmModalProps {
    isOpen: boolean;
    qty: number;
    isLoading: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const DeleteInventoryConfirmModal = ({
    isOpen,
    qty,
    isLoading,
    onClose,
    onConfirm,
}: DeleteInventoryConfirmModalProps) => (
    <Modal size="small" isOpen={isOpen} onClose={onClose}>
        <Modal.Header>Permanently Delete Inventory</Modal.Header>
        <Modal.Body>
            <p className="text-gray-600 text-center">
                You are about to permanently remove {pluralize("item", qty, true)} from inventory. This action cannot be
                undone.
            </p>
        </Modal.Body>
        <Modal.Footer>
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <SubmitButton color="danger" isLoading={isLoading} onClick={onConfirm}>
                Remove from Inventory
            </SubmitButton>
        </Modal.Footer>
    </Modal>
);
